import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';

interface CacheEntry<T> {
  observable: Observable<T>;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class CacheService {
  private cache = new Map<string, CacheEntry<any>>();
  private defaultTTL = 5 * 60 * 1000; // 5 minutes in ms

  // Event bus for cache invalidation notifications
  private invalidationSubject = new Subject<string>();
  public invalidation$ = this.invalidationSubject.asObservable();

  /**
   * Get or create a cached observable.
   * If the cache entry exists and hasn't expired, returns the cached observable.
   * Otherwise, executes the factory function and caches the result.
   *
   * @param key - Unique cache key
   * @param factory - Function that returns the observable to cache
   * @param ttl - Time to live in milliseconds (optional, defaults to 5 minutes)
   * @param forceRefresh - If true, bypasses cache and fetches fresh data
   */
  get<T>(
    key: string,
    factory: () => Observable<T>,
    ttl: number = this.defaultTTL,
    forceRefresh: boolean = false,
  ): Observable<T> {
    const now = Date.now();
    const entry = this.cache.get(key);

    // Return cached observable if valid and not forcing refresh
    if (!forceRefresh && entry && now - entry.timestamp < ttl) {
      return entry.observable;
    }

    // Create new cached observable
    const observable = factory().pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.cache.set(key, {
      observable,
      timestamp: now,
    });

    return observable;
  }

  /**
   * Invalidate a specific cache entry by key.
   */
  invalidate(key: string): void {
    this.cache.delete(key);
    this.invalidationSubject.next(key);
  }

  /**
   * Invalidate all cache entries matching a prefix.
   * Useful for invalidating all user-related caches: invalidateByPrefix('user:')
   */
  invalidateByPrefix(prefix: string): void {
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => {
      this.cache.delete(key);
      this.invalidationSubject.next(key);
    });
  }

  /**
   * Clear all cached entries.
   */
  clear(): void {
    this.cache.clear();
    this.invalidationSubject.next('*');
  }

  /**
   * Check if a cache entry exists and is valid.
   */
  has(key: string, ttl: number = this.defaultTTL): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;
    return Date.now() - entry.timestamp < ttl;
  }
}
